/**
 * Yard Capacity Service — Matches Xamarin MiddlewareService.GetYardCapacityUtilizations()
 *
 * Endpoint: NBL_YMS_GET_YRD_CAP_UTILI/1.0/getCapacityUtilization?org={orgId}&userId={userId}
 * Method: POST (org and userId as query params, TraceData in body)
 * Auth: Bearer or Basic (based on APP_MA_AUTH_TYPE setting)
 */

import { YMS_API_CONFIG } from '@/constants';
import { store } from '@/store';

import { getAuthHeader, oicFetch } from './ManhattanService';
import { mapYardCapacityUtilization } from './types/yardCapacityTypes';

import type { OICYardCapacityResponse, YardCapacityUtilization } from './types/yardCapacityTypes';

// ─────────────────────────────────────────────────────────────────
// Helpers — same pattern as MoveTaskService
// ─────────────────────────────────────────────────────────────────

function getOICBaseUrl(): string {
  // DEV: Vite proxy handles CORS. PROD: Azure Function proxy avoids browser CORS.
  return import.meta.env.DEV ? '/middleware_new/' : '/api/oic-proxy/';
}

function getOrgId(): string {
  return String(store.getState().user.plant?.org_id ?? '');
}

function getUserId(): string {
  const user = store.getState().user.user;
  if (!user) return '';
  return `${user.country_code}${user.mobile_no}`;
}

// ─────────────────────────────────────────────────────────────────
// API Call — Matches Xamarin:
// _client.Request(MAYardCapacityUtilizationAPI + Org + "&userId=" + UserId)
//        .WithOAuthBearerToken(accessToken.Token)
// ─────────────────────────────────────────────────────────────────

export async function getYardCapacityUtilization(): Promise<YardCapacityUtilization | null> {
  const baseUrl = getOICBaseUrl();
  const orgId = getOrgId();
  const userId = getUserId();

  if (!baseUrl) {
    throw new Error('MA_API_BASE_URL is not configured');
  }

  // Matches Xamarin: MAYardCapacityUtilizationAPI + Org + "&userId=" + UserId
  const url = `${baseUrl}${YMS_API_CONFIG.MAYardCapacityUtilizationAPI}?org=${encodeURIComponent(orgId)}&userId=${encodeURIComponent(userId)}`;

  const authHeader = await getAuthHeader();

  const response = await oicFetch(url, {
    method: 'GET',
    headers: {
      Authorization: authHeader,
    },
  });

  if (!response.ok) {
    throw new Error(
      `OIC Yard Capacity API error: ${String(response.status)} ${response.statusText}`
    );
  }

  const raw = (await response.json()) as OICYardCapacityResponse;

  // OIC returns lowercase "payload" with underscore_case fields in "Data"
  const dto = raw.payload.Body.Data;
  if (!dto) return null;

  return mapYardCapacityUtilization(dto);
}

// ─────────────────────────────────────────────────────────────────
// Export facade
// ─────────────────────────────────────────────────────────────────

export const yardCapacityApi = {
  getYardCapacityUtilization,
};
